import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { SCORECARD, SCORECARD_VOCAB } from "@shared/scorecard";
import { Note } from "../../../primitives/data-display/Note";
import { Meta } from "../../../primitives/data-display/Meta";
import { AIQuote } from "../../../primitives/data-display/AIQuote";
import { Panel } from "../../../primitives/surfaces/Panel";
import { gaps } from "../../../primitives/spacing";
import OverrideMode from "./OverrideMode";
import type { PrevalidatedField } from "../../../../lib/corpusApi";
import {
  FieldPanelHeader,
  FieldPanelActions,
  HelpPanel,
  PanelContainedButton,
  PanelRoot,
  ChipRow,
  ItalicNote,
  type FieldPanelBase,
} from "./panel_shared";

export interface PrevalidatedFieldPanelProps extends FieldPanelBase {
  /** The pipeline's fast-path settlement for this field. */
  prevalidated: PrevalidatedField;
  /** Approve the pre-validated pipeline value as-is. */
  onApprove: () => void;
}

const DesktopHint = styled(Stack)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: { display: "flex" },
}));

/**
 * A field the deterministic pipeline settled from an authoritative source (fast-path), shown
 * pre-approved: the reviewer confirms with one key or overrides. Distinct from the multi-candidate
 * panel — there is one settled value here, not competing model drafts — and it still passes through
 * the human gate rather than being auto-accepted unseen.
 */
export function PrevalidatedFieldPanel({
  field,
  prevalidated,
  override,
  saving,
  helpOpen,
  onApprove,
  onOverride,
  onMarkEmpty,
  onReject,
  onHelpToggle,
}: PrevalidatedFieldPanelProps) {
  const vocab = SCORECARD_VOCAB[field] ?? [];
  const config = SCORECARD[field];
  const labelDescriptions = config?.labelDescriptions;
  const hasLabels = prevalidated.labels.length > 0;
  const confidencePct = Math.round(prevalidated.confidence * 100);

  const badge = (
    <Tooltip
      title={`Settled by the pipeline (${prevalidated.method}, ${confidencePct}% confidence) — pre-validated, not model-drafted`}
    >
      <Chip label={`Pre-validated · ${confidencePct}%`} size="small" color="success" />
    </Tooltip>
  );

  return (
    <PanelRoot gap={gaps.base}>
      <FieldPanelHeader
        field={field}
        badge={badge}
        helpOpen={helpOpen}
        onHelpToggle={onHelpToggle}
      />

      <div style={{ display: helpOpen ? undefined : "none" }}>
        <HelpPanel
          description={config?.description}
          vocab={vocab}
          labelDescriptions={labelDescriptions}
        />
      </div>

      {override.mode ? (
        <OverrideMode
          labels={vocab}
          selected={override.selection}
          multiple={!config?.singleSelect}
          focusIndex={override.focusIndex}
          onFocusChange={override.onFocusChange}
          onChange={override.onSelectionChange}
          onConfirm={override.onConfirm}
          onCancel={override.onCancel}
        />
      ) : (
        <>
          <Panel tone="muted" pad="sm">
            <Stack gap={gaps.tight}>
              <Meta>Pipeline settled ({prevalidated.method}):</Meta>
              {hasLabels ? (
                <ChipRow direction="row" gap={gaps.tight}>
                  {prevalidated.labels.map((label) => (
                    <Chip key={label} label={label} size="small" />
                  ))}
                </ChipRow>
              ) : (
                <ItalicNote>Nothing extracted — your call</ItalicNote>
              )}
            </Stack>
          </Panel>

          {prevalidated.evidence && (
            <Stack gap={gaps.tight}>
              <Meta>Evidence</Meta>
              <AIQuote>{prevalidated.evidence}</AIQuote>
            </Stack>
          )}

          <Divider />

          <FieldPanelActions
            primary={
              <PanelContainedButton
                variant="contained"
                size="small"
                onClick={onApprove}
                disabled={!hasLabels || saving}
              >
                ↵ Approve
              </PanelContainedButton>
            }
            saving={saving}
            onOverride={onOverride}
            onMarkEmpty={onMarkEmpty}
            onReject={onReject}
          />

          {/* Keyboard shortcut hint — desktop only */}
          <DesktopHint>
            <Note>
              ↵ approve · O override · E mark empty · ? help · each action saves and advances
            </Note>
          </DesktopHint>
        </>
      )}
    </PanelRoot>
  );
}
